import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { threatsAPI } from '@/lib/api';
import { cn, getSeverityColor, formatDate } from '@/lib/utils';
import {
  Search, Shield, Activity, Crosshair, RefreshCw, Eye, Ban,
  AlertTriangle, CheckCircle, Globe, Network, Zap, Copy, Clock
} from 'lucide-react';

const getActionColor = (action) => {
  if (action === 'blocked') return 'bg-success/20 text-success border-success/30';
  if (action === 'detected') return 'bg-warning/20 text-warning border-warning/30';
  return 'bg-muted text-muted-foreground';
};

const getActionIcon = (action) => {
  if (action === 'blocked') return CheckCircle;
  return AlertTriangle;
};

export function ThreatsPage() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [stats, setStats] = useState({ total_blocked: 0, total_detected: 0, active_threats: 0, traffic_analyzed: '0TB' });
  const [isLoading, setIsLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, severityFilter, actionFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await threatsAPI.getEvents();
      setEvents(res.data.events || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error('Error loading threats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = [...events];
    if (severityFilter !== 'all') filtered = filtered.filter(e => e.severity === severityFilter);
    if (actionFilter !== 'all') filtered = filtered.filter(e => e.action === actionFilter);
    setFilteredEvents(filtered);
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const categoryStats = events.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen" data-testid="threats-page">
      <Header title="Threat Detection (IDS/IPS)" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-warning/10 border-warning/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">IDS Detecções</p>
                  <p className="text-3xl font-mono font-bold text-warning">{stats.total_detected}</p>
                </div>
                <Search className="h-8 w-8 text-warning opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-success/10 border-success/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">IPS Bloqueados</p>
                  <p className="text-3xl font-mono font-bold text-success">{stats.total_blocked}</p>
                </div>
                <Shield className="h-8 w-8 text-success opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-destructive/10 border-destructive/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Ameaças Ativas</p>
                  <p className="text-3xl font-mono font-bold text-destructive">{stats.active_threats}</p>
                </div>
                <Crosshair className="h-8 w-8 text-destructive opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-info/10 border-info/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Tráfego Analisado</p>
                  <p className="text-3xl font-mono font-bold text-info">{stats.traffic_analyzed}</p>
                </div>
                <Activity className="h-8 w-8 text-info opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
                Distribuição por Categoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(categoryStats).slice(0, 6).map(([cat, count]) => {
                  const pct = Math.round((count / events.length) * 100);
                  return (
                    <div key={cat}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{cat}</span>
                        <span className="font-mono text-muted-foreground">{count} ({pct}%)</span>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
                Status IDS/IPS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: 'Suricata Engine', status: 'RUNNING', color: 'text-success' },
                { label: 'Regras Carregadas', status: '47,234', color: 'text-info' },
                { label: 'Última Atualização', status: 'há 2h', color: 'text-muted-foreground' },
                { label: 'Modo de Operação', status: 'IDS/IPS', color: 'text-primary' },
                { label: 'Interface Monitorada', status: 'eth0, wan0', color: 'text-muted-foreground' },
                { label: 'Taxa de Detecção', status: '99.2%', color: 'text-success' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-secondary/50 rounded">
                  <span className="text-sm text-muted-foreground">{item.label}</span>
                  <span className={cn("font-mono text-sm font-medium", item.color)}>{item.status}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-card/50 border-border">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Severidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Ação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="blocked">Bloqueado</SelectItem>
                  <SelectItem value="detected">Detectado</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadData} data-testid="refresh-threats">
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Events Table */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
              Eventos IDS/IPS ({filteredEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ação</TableHead>
                    <TableHead>Assinatura</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Severidade</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Protocolo</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEvents.map((event) => {
                    const ActionIcon = getActionIcon(event.action);
                    return (
                      <TableRow
                        key={event.id}
                        className="hover:bg-secondary/50 cursor-pointer"
                        onClick={() => setSelectedEvent(event)}
                        data-testid={`threat-event-${event.id}`}
                      >
                        <TableCell>
                          <Badge className={cn("border flex items-center gap-1 w-fit", getActionColor(event.action))}>
                            <ActionIcon className="h-3 w-3" />
                            {event.action?.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs font-mono text-muted-foreground line-clamp-1 max-w-[200px]">
                            {event.signature}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{event.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("border", getSeverityColor(event.severity))}>
                            {event.severity?.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-mono">{event.src_ip}</p>
                            <p className="text-xs text-muted-foreground">{event.src_country}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-mono">{event.dest_ip}</p>
                            <p className="text-xs text-muted-foreground">:{event.dest_port}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">{event.protocol}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {formatDate(event.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Event Detail Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Crosshair className="h-6 w-6 text-warning" />
              <span className="font-mono text-sm">{selectedEvent?.category}</span>
              <Badge className={cn("border", getSeverityColor(selectedEvent?.severity))}>
                {selectedEvent?.severity?.toUpperCase()}
              </Badge>
              <Badge className={cn("border", getActionColor(selectedEvent?.action))}>
                {selectedEvent?.action?.toUpperCase()}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="bg-secondary">
                <TabsTrigger value="details">Detalhes</TabsTrigger>
                <TabsTrigger value="network">Rede</TabsTrigger>
                <TabsTrigger value="actions">Ações</TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[420px] mt-4">
                <TabsContent value="details" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-2">Assinatura</h4>
                      <code className="text-sm font-mono">{selectedEvent.signature}</code>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Evento</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Categoria</span><Badge variant="outline">{selectedEvent.category}</Badge></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Severidade</span><Badge className={cn("border", getSeverityColor(selectedEvent.severity))}>{selectedEvent.severity?.toUpperCase()}</Badge></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Ação</span><Badge className={cn("border", getActionColor(selectedEvent.action))}>{selectedEvent.action?.toUpperCase()}</Badge></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Interface</span><span className="font-mono text-sm">{selectedEvent.interface}</span></div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Tráfego</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Protocolo</span><Badge variant="outline" className="font-mono">{selectedEvent.protocol}</Badge></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Bytes</span><span className="font-mono text-sm">{selectedEvent.bytes?.toLocaleString()}</span></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Pacotes</span><span className="font-mono text-sm">{selectedEvent.packets}</span></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Timestamp</span><span className="font-mono text-xs">{formatDate(selectedEvent.timestamp)}</span></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="network" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-destructive/10 border-destructive/30">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-destructive mb-3 flex items-center gap-2">
                          <Globe className="h-4 w-4" /> Origem
                        </h4>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-sm bg-background px-2 py-1 rounded flex-1">{selectedEvent.src_ip}</code>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(selectedEvent.src_ip)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">País</span><span className="font-mono text-sm">{selectedEvent.src_country}</span></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Porta</span><span className="font-mono text-sm">{selectedEvent.src_port}</span></div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                          <Network className="h-4 w-4" /> Destino
                        </h4>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <code className="font-mono text-sm bg-background px-2 py-1 rounded flex-1">{selectedEvent.dest_ip}</code>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(selectedEvent.dest_ip)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Porta</span><span className="font-mono text-sm">{selectedEvent.dest_port}</span></div>
                          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Protocolo</span><Badge variant="outline" className="font-mono text-xs">{selectedEvent.protocol}</Badge></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="actions" className="space-y-4 mt-0">
                  <Card className="bg-primary/10 border-primary/30">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-primary mb-2 flex items-center gap-2">
                        <Zap className="h-4 w-4" /> Ações Recomendadas
                      </h4>
                      <p className="text-sm">Baseado na análise deste evento, execute as ações abaixo.</p>
                    </CardContent>
                  </Card>

                  <div className="space-y-3">
                    {[
                      { title: 'Bloquear IP de origem no firewall', cmd: `iptables -A INPUT -s ${selectedEvent.src_ip} -j DROP`, priority: 'high' },
                      { title: 'Verificar histórico de conexões', cmd: `grep "${selectedEvent.src_ip}" /var/log/suricata/fast.log | tail -50`, priority: 'medium' },
                      { title: 'Adicionar à blacklist de Threat Intel', cmd: `echo "${selectedEvent.src_ip}" >> /etc/suricata/rules/blacklist.txt`, priority: 'medium' },
                      { title: 'Atualizar regras do Suricata', cmd: 'suricata-update && systemctl reload suricata', priority: 'low' },
                    ].map((action, i) => (
                      <Card key={i} className="bg-secondary/50">
                        <CardContent className="pt-3 pb-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{action.title}</span>
                            <Badge variant="outline" className="text-xs">Prioridade: {action.priority}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-background p-2 rounded font-mono overflow-x-auto">{action.cmd}</code>
                            <Button size="icon" variant="ghost" onClick={() => copyToClipboard(action.cmd)}>
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button className="flex-1" variant="destructive">
                      <Ban className="h-4 w-4 mr-2" />
                      Bloquear IP Agora
                    </Button>
                    <Button variant="outline">
                      <Clock className="h-4 w-4 mr-2" />
                      Criar Ticket
                    </Button>
                  </div>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ThreatsPage;
